import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  DetailTypography,
  HeroBannerStack,
  StatusBoxesStyle,
  TitleTypography
} from "./HeroBanner.styles";
import { HeroBannerProps } from "./HeroBanner.types";
import {
  CardCta,
  CardSignature,
  CtaOverlay,
  VideoCover
} from "../../components";
import { StatusBoxes } from "../../components/CarouselCardRestyle/StatusBoxes.component";
import { TopicTags } from "../../components/CarouselCardRestyle/TopicTags.component";
import { NavigationBreadcrumbs } from "../../components/NavigationBreadcrumbs/NavigationBreadcrumbs.component";
import {
  LEVEL, TAB_ITEMS
} from "../../consts";
import {  useType } from "../../hooks";
import { useNavbarStore } from "../../stores";
import { hasPassedExpiration } from "../../utils/datetime";
import { getPreviousRoute } from "../../utils/general";
import { TruncatedText } from "../TruncatedText";

export function HeroBanner({
  badge,
  certificate,
  booked,
  categories,
  coverPublicURL,
  coverVideoPublicUrl,
  courseId=undefined,
  duration,
  ecmRegistration,
  ecmType,
  editionNumber,
  editionVirtualLink,
  enrollType,
  expirationDate,
  id,
  ecm_specialization,
  editionsLength,
  isEnable,
  isFavourite,
  isMandatory,
  isStandAlone,
  isSurvey,
  isTest,
  isToj,
  learningObjectType,
  learningObjectTypology,
  meetingDetails,
  percentageOfCompletion,
  rating,
  rootId=undefined,
  setOpenTojModal,
  shortDescription,
  startDate,
  endDate,
  setOpenModalEcm,
  status,
  title,
  isCurrentSession,
  completable,
  canAccessNow,
  credits,
  isLinkRelative,
  link,
  attributes,
  attributesIds,
  certificates,
  ...props
}: HeroBannerProps) {

  const theme = useTheme();
  const { t } = useTranslation();
  const {  pathname } = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const typeObjectLearning = useType(learningObjectType, learningObjectTypology);
  const [videoVisible, setVideoVisible] = useState(Boolean(coverVideoPublicUrl));
  const navbarItem = useNavbarStore(state => state.item);
  const previousPath = getPreviousRoute({ id, rootId, courseId, path: pathname, section: navbarItem, learningObjectTypology });

  const cardCtaVisibility = (() => {
    switch (learningObjectTypology) {
    case LEVEL.DA_PHYSICAL_CLASS:
      return {
        disabled: status === "C" || status === "W" || false,
        visibility: status !== "N" && status !== "W"
      };
    case LEVEL.DA_VIRTUAL_CLASS:
      return {
        disabled: false,
        visibility: status !== "N" && status !== "W"
      };
    case LEVEL.DA_MEETING:
      return {
        disabled:true,
        visibility: false
      };
    default:
      return {
        disabled: isTest && status === "C" || status === "W" || false,
        visibility: true
      };
    }
  })();

  const isPAthOrCourse = learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE;

  return (

    <Box
      role="main"
      sx={ {
        background:!videoVisible
          ?`linear-gradient(to left, transparent 0%, ${theme.customColors.backgroundPrimary} 57%), url(${coverPublicURL})`
          : `linear-gradient(to left, transparent 0%, ${theme.customColors.backgroundPrimary} 57%)`,
        backgroundPosition: "center",
        backgroundRepeat:"no-repeat",
        backgroundSize: "cover",
        position:"relative"
      } }
    >
      {
        videoVisible && coverVideoPublicUrl ?
          <VideoCover
            sx={ {
              "&:before": {
                background: `linear-gradient(-90deg, ${theme.customColors.backgroundPrimary} 0%, ${theme.customColors.backgroundPrimary} 100%)`
              }
            } }
            img={ coverPublicURL.toString() }
            onEnded={ ()=> { setVideoVisible(false)} }
            url={ coverVideoPublicUrl }
          /> : null
      }
      <StatusBoxesStyle aria-hidden="true" tabIndex={ -1 } isMobile={ isMobile }>
        <StatusBoxes
          id={ id }
          isMandatory={ isMandatory }
          isBooked={ booked && (
            learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
              || learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
              || isToj
          ) }
          endDate={ endDate }
          isCompleted={ status === "C" }
          isExpired={ hasPassedExpiration(expirationDate) }
          expirationDate={ expirationDate }
          startDate={ startDate }
          learningObjectTypology={ learningObjectTypology }
          isFavourite={ isFavourite }
          attributes={ attributes }
          usedInsideBigBanner={ true }
        />
      </StatusBoxesStyle>

      <HeroBannerStack
        component="section"
        gap={ 2 }
        position={ "relative" }
        { ...props }
        sx={ {
          padding: `${(
            (status === "C" || isMandatory) &&
            (isPAthOrCourse && !completable)
          ) ? "240px" : "160px"} 44px 77px`,
          [theme.breakpoints.down("sm")]: {
            padding: `${(
              (status === "C" || isMandatory) &&
              (isPAthOrCourse && !completable)
            ) ? "200px" : "120px"} 20px 20px`
          }
        } }
      >
        { isPAthOrCourse && !completable && (
          <Box
            key={ t("completable-text") }
            tabIndex={ -1 }
            sx={ {
              position: "absolute",
              top: !isMobile ? "160px" : "80px",
              left: "2.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.2rem 0.4375rem",
              gap: "0.25rem",
              border: `1px solid ${theme.customColors.borderError}`,
              backgroundColor: theme.customColors.backgroundWarning,
              borderRadius: "0.25rem",
              width: isMobile ? "calc(100% - 3rem)" : "fit-content"
            } }
          >
            <Typography tabIndex={ -1 } id={ `${id}-card-statusBox` } sx={ { fontSize: "0.75rem", fontWeight: "600" } }
              color={ theme.customColors.textTag }>
              { t("completable-text") }
            </Typography>
          </Box>
        ) }
        <NavigationBreadcrumbs learningObjectTypology={ learningObjectTypology } previousPath={ previousPath }/>
        {
          isTest ?

            <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
              {   t("test") }
            </span>
            :

            isSurvey ?
              <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                {   t("survey") }
              </span> :
              null
        }
        {
          ecm_specialization && ecmType ?

            <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
              { ecmType.toUpperCase() }
            </span> :
            typeObjectLearning && !isTest && !isSurvey  ?
              <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                { typeObjectLearning }
              </span>
              : null
        }
        <TitleTypography  variant={ "h1" } sx={ {
          maxWidth: "80%",
          whiteSpace: "normal",
          wordWrap: "break-word",
          [theme.breakpoints.down("md")]: {
            fontSize: "3rem"
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem"
          }
        } }>
          <TruncatedText text={ title } maxLength={ 120 }/>
        </TitleTypography>
        <DetailTypography variant={ "h3" }>
          <TruncatedText text={ `${shortDescription}` } maxLength={ 120 } />
        </DetailTypography>
        <CardSignature
          ecm_specialization={ ecm_specialization }
          editionNumber={ editionNumber }
          expirationDate={ expirationDate }
          duration={ duration }
          startDate={ startDate }
          endDate={ endDate }
          gap={ 2 }
          isToj={ isToj }
          learningObjectTypology={ learningObjectTypology }
          meetingDetails={ meetingDetails }
          canAccessNow={ canAccessNow }
        />
        { (categories && categories?.length > 0) &&
          <TopicTags
            topicTags={ categories }
          />
        }
        {
          cardCtaVisibility.visibility ?
            <CardCta
              badge={ badge }
              certificate={ certificate }
              disabled={ !isEnable ||
                (cardCtaVisibility.disabled ||
                  status === "R") ||
                  (learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS &&
                    (!editionsLength || (status === "E" && !isCurrentSession)))
              }
              ecmRegistration={ ecmRegistration }
              editionsLength={ editionsLength }
              enrollType={ enrollType }
              className={ "card-cta" }
              courseId={ courseId }
              id={ id }
              ecm_specialization={ ecm_specialization }
              maxWidth={ "600px" }
              isStandAlone={ isStandAlone }
              learningObjectTypology={ learningObjectTypology }
              learningObjectType={ learningObjectType }
              linkVirtualClass={ editionVirtualLink }
              percentageOfCompletion={ percentageOfCompletion }
              isTest={ isTest }
              isSurvey={ isSurvey }
              rootId={ rootId }
              simple={ false }
              status={ status }
              triggerOpenTabs={ openEditions }
              isCard={ false }
              setOpenRegistrationModal={ setOpenModalEcm }
              setOpenTojModal={ setOpenTojModal }
              canAccessNow={ canAccessNow }
              credits={ credits }
              isLinkRelative={ isLinkRelative }
              link={ link }
              attributesIds={ attributesIds }
              certificates={ certificates }
            /> :
            null
        }

        <CtaOverlay
          isStandAlone={ isStandAlone }
          isFavourite={ isFavourite }
          id={ id }
          learningObjectTypology={ learningObjectTypology }
          rating={ rating }
          share={ true }
          courseId={ courseId }
          rootId={ rootId }
        />
      </HeroBannerStack>

    </Box>
  );

  function openEditions() {
    const tabsComponent = document.querySelector(".MuiTabs-flexContainer") as HTMLDivElement;

    if (tabsComponent) {
      const tabs = tabsComponent.querySelectorAll("button") as NodeListOf<HTMLButtonElement>;
      const labelToCheck =
        learningObjectTypology === LEVEL.COURSE ? TAB_ITEMS.ACTIVITIES
          : learningObjectTypology === LEVEL.PATH ? "modules"
            : TAB_ITEMS.EDITIONS;
      if (labelToCheck !== "modules"){
        tabs.forEach((tab) => {
          if (tab.id.includes(labelToCheck)) {
            tab.click();
            tab.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
          }
        });
      }
      else {
        const modules = document.querySelector(".modules-tab");
        if(modules){
          modules.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      }
    }
  }
}

